import { Box, ButtonBase, Typography, alpha } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { Check } from "@mui/icons-material";
import { ReactNode } from "react";

export interface OptionCardItem {
  value: string;
  label: ReactNode;
  sub: string;
  icon: ReactNode;
  /** data-testid de la card, para pruebas automatizadas. */
  testId: string;
  /** Opción que ya no se puede elegir (p. ej. la vía no elegida en `DecisionIngreso`). Solo `layout="list"`. */
  disabled?: boolean;
}

/**
 * - `list`: una opción por fila, ícono en cuadro de 40px y check a la derecha (`DecisionIngreso`).
 * - `grid`: `.radio-card` de los cotizadores v2 en `columns` columnas, ícono en círculo de 44px
 *   (antes `RadioCardGroupCotizador`).
 * - `tiles`: tarjetas apiladas (ícono → texto) en cuadrícula automática, para muchas opciones cortas
 *   (antes `RadioCardGridCotizador`, tipos de equipo de Mercader).
 */
export type OptionCardLayout = "list" | "grid" | "tiles";

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: OptionCardItem[];
  layout?: OptionCardLayout;
  /** Columnas de `layout="grid"`. */
  columns?: number;
}

/** Check circular de `grid` y `tiles`. */
const CheckCotizador = ({ on, size, iconSize }: { on: boolean; size: number; iconSize: number }) => (
  <Box
    sx={{
      flexShrink: 0,
      width: size,
      height: size,
      borderRadius: "50%",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: on ? "primary.accent" : "tokens.surface2",
      border: "1.5px solid",
      borderColor: on ? "primary.accent" : "#D5DCE4",
      color: on ? "#fff" : "transparent",
    }}
  >
    <Check sx={{ fontSize: iconSize }} />
  </Box>
);

/** Ícono en círculo de `grid` y `tiles`. */
const IconCotizador = ({ on, size, children }: { on: boolean; size: number; children: ReactNode }) => (
  <Box
    sx={{
      flexShrink: 0,
      width: size,
      height: size,
      borderRadius: "50%",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: on ? "primary.accent" : "tokens.surface2",
      color: on ? "#fff" : "tokens.textTertiary",
      border: "1px solid",
      borderColor: on ? "primary.accent" : "divider",
      transition: "background-color .2s ease, color .2s ease",
    }}
  >
    {children}
  </Box>
);

/** Borde, fondo y hover comunes a las tarjetas de `grid` y `tiles`. */
const cardCotizadorSx = (on: boolean) => ({
  border: "1.5px solid",
  borderColor: on ? "primary.accent" : "divider",
  borderRadius: "8px",
  bgcolor: on ? "primary.base" : "background.paper",
  boxShadow: (theme: Theme) => (on ? `0 0 0 1px ${theme.palette.primary.accent} inset` : "none"),
  transition: "border-color .18s ease, background-color .18s ease",
  "&:hover": { borderColor: "primary.accent", bgcolor: "primary.base" },
});

const OptionCardGrid = ({ value, onChange, options, columns = 1 }: Props) => (
  <Box role="radiogroup" sx={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 1.5 }}>
    {options.map((opt) => {
      const on = value === opt.value;
      return (
        <ButtonBase
          key={opt.value}
          role="radio"
          aria-checked={on}
          onClick={() => onChange(opt.value)}
          data-testid={opt.testId}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            textAlign: "left",
            gap: 2,
            p: "18px 20px",
            ...cardCotizadorSx(on),
          }}
        >
          <IconCotizador on={on} size={44}>
            {opt.icon}
          </IconCotizador>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14.5, fontWeight: 600, letterSpacing: "-0.01em", color: "primary.dark" }}>
              {opt.label}
            </Typography>
            <Typography variant="body2" color="tokens.textTertiary" sx={{ mt: 0.25 }}>
              {opt.sub}
            </Typography>
          </Box>
          <CheckCotizador on={on} size={22} iconSize={14} />
        </ButtonBase>
      );
    })}
  </Box>
);

// Cuatro por fila cuando hay ancho y dos cuando no (`minmax(150px, 1fr)`).
const OptionCardTiles = ({ value, onChange, options }: Props) => (
  <Box
    role="radiogroup"
    sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 1.5 }}
  >
    {options.map((opt) => {
      const on = value === opt.value;
      return (
        <ButtonBase
          key={opt.value}
          role="radio"
          aria-checked={on}
          onClick={() => onChange(opt.value)}
          data-testid={opt.testId}
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            textAlign: "left",
            gap: "9px",
            p: "13px 14px 14px",
            ...cardCotizadorSx(on),
          }}
        >
          <IconCotizador on={on} size={32}>
            {opt.icon}
          </IconCotizador>
          <Box sx={{ width: "100%", pr: 2.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, color: "primary.dark" }}>
              {opt.label}
            </Typography>
            <Typography
              variant="body3"
              color="tokens.textTertiary"
              sx={{ display: "block", mt: 0.25, lineHeight: 1.4 }}
            >
              {opt.sub}
            </Typography>
          </Box>
          <Box sx={{ position: "absolute", top: 11, right: 11, display: "flex" }}>
            <CheckCotizador on={on} size={18} iconSize={12} />
          </Box>
        </ButtonBase>
      );
    })}
  </Box>
);

/**
 * Replica `.radio-card` de styles.css (sección "premium fintech", la que gana en
 * cascada): tile de icono 40×40 que se invierte al seleccionar y círculo de check
 * a la derecha. La selección va en el color de selección de la marca (`primary.accent`,
 * el `#81AB77` de Flux) sobre su tono claro (`primary.base`), como en los cotizadores.
 * Con `layout` cubre también las tarjetas de los cotizadores v2 (`grid` y `tiles`).
 *
 * Convive con `RadioCardGroup`, que quedó con el spec viejo (sin tile ni check) y
 * sigue en Cotizador y ConexionCuentas.
 */
export const OptionCardGroup = (props: Props) => {
  if (props.layout === "grid") return <OptionCardGrid {...props} />;
  if (props.layout === "tiles") return <OptionCardTiles {...props} />;

  const { value, onChange, options } = props;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {options.map((opt) => {
        const on = value === opt.value;
        return (
          <Box
            key={opt.value}
            onClick={() => !opt.disabled && onChange(opt.value)}
            data-testid={opt.testId}
            aria-disabled={opt.disabled}
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              gap: "16px",
              p: "16px 18px",
              borderRadius: "10px",
              border: "1.5px solid",
              borderColor: on ? "primary.accent" : "divider",
              bgcolor: on ? "primary.base" : "background.paper",
              // El inset simula el borde de 2px del estado activo sin recalcular el layout.
              boxShadow: on ? `inset 0 0 0 1px ${theme.palette.primary.accent}` : "none",
              cursor: opt.disabled ? "not-allowed" : "pointer",
              opacity: opt.disabled ? 0.5 : 1,
              userSelect: "none",
              transition: "border-color .18s ease, background-color .18s ease, box-shadow .2s ease",
              "&:hover": opt.disabled
                ? {}
                : {
                    borderColor: on ? "primary.accent" : alpha(theme.palette.primary.accent, 0.7),
                    bgcolor: on ? "primary.base" : alpha(theme.palette.primary.accent, 0.06),
                  },
            })}
          >
            <Box
              sx={(theme) => ({
                flexShrink: 0,
                width: 40,
                height: 40,
                borderRadius: "9px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid",
                borderColor: on ? "primary.accent" : "divider",
                bgcolor: on ? "primary.accent" : "tokens.surface2",
                color: on ? "#FFF" : "tokens.textTertiary",
                boxShadow: on ? `0 4px 12px -2px ${alpha(theme.palette.primary.accent, 0.4)}` : "none",
                transition: "background-color .2s ease, color .2s ease, border-color .2s ease",
                // El tamaño lo fija el tile para que el consumidor pase el icono sin props extra.
                "& > svg": { fontSize: 20 },
              })}
            >
              {opt.icon}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 14.5, fontWeight: 600, letterSpacing: "-0.01em", color: "primary.dark" }}>
                {opt.label}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: "tokens.textTertiary", mt: "2px", lineHeight: 1.5 }}>
                {opt.sub}
              </Typography>
            </Box>

            <Box
              sx={{
                flexShrink: 0,
                width: 22,
                height: 22,
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1.5px solid",
                borderColor: on ? "primary.accent" : "tokens.borderStrong",
                bgcolor: on ? "primary.accent" : "background.paper",
                color: "#FFF",
                transition: "background-color .2s ease, border-color .2s ease",
              }}
            >
              {on && <Check sx={{ fontSize: 12 }} />}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
